import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Document, db } from "../db";
import { getJWT } from "./jwt";
import { addToDownloadQueue } from "./FileDownloader";
import { AppConfiguration, defaultAppConfiguration, readConfiguration, useAppConfiguration } from "./AppConfiguration";

export type DocSynchroniserState = "inSync" | "networkError" | "unauthorized" | "synchronising";
// export interface DocymentSync
export interface DocumentSyncStatus {
  status: DocSynchroniserState;
  diskSpaceLimited: boolean;
}

export class DocListSynchroniser implements DocumentSyncStatus {
  static instance = new DocListSynchroniser();
  status: DocSynchroniserState;
  stateListener: ((newStatus: DocumentSyncStatus) => void) | undefined;
  diskSpaceLimited: boolean;

  private constructor() {
    this.status = navigator.onLine ? "inSync" : "networkError";
    this.diskSpaceLimited = false;

    window.addEventListener("online", (event) => {
      console.log("Online");
      this.doSync();
    });
    window.addEventListener("offline", (event) => {
      this.setState("networkError");
      console.log("Offline");
    });
  }

  setStateListener(l: undefined | ((newStatus: DocumentSyncStatus) => void)) {
    this.stateListener = l;
  }

  private sendStateUpdate() {
    if (this.stateListener) {
      this.stateListener({ status: this.status, diskSpaceLimited: this.diskSpaceLimited });
    }
  }

  private setState(st: DocSynchroniserState) {
    if (st !== this.status) {
      console.log("setting status to ", st);
      this.status = st;
      this.sendStateUpdate();
    }
  }

  setDiskSpaceLimited(val: boolean) {
    if (val !== this.diskSpaceLimited) {
      this.diskSpaceLimited = val;
      this.sendStateUpdate();
    }
  }

  async getChanges(since: number | undefined): Promise<Document[]> {
    const sp: URLSearchParams = new URLSearchParams();
    if (since) {
      sp.set("since", since.toString());
    }
    const res = await fetch(`/sync?${sp.toString()}`, {
      redirect: "manual",
    });

    if (!res.ok) {
      throw new Error(`Error fetching response: ${res.status}: ${res.statusText}: ${await res.text()}`);
    }

    const resJson = (await res.json()) as Document[];
    return resJson;
  }

  async doSync() {
    if (this.status === "synchronising") {
      console.log("Ingoring attempt to synchronise while one already running");
      return;
    }
    if (!navigator.onLine) {
      this.setState("networkError");
      return;
    }
    this.setState("synchronising");
    try {
      let jwt;
      try {
        jwt = await getJWT();
      } catch (error: any) {
        console.log("Error getting JWT", error);
        if (error.message.includes("Network error")) {
          this.setState("networkError");
        } else {
          this.setState("unauthorized");
        }
        return;
      }
      await this.syncDocumentMeta();

      // Update localStorage with current date and time
      const currentTime = new Date().toISOString();
      localStorage.setItem("lastSyncTime", currentTime);
      this.setState("inSync");
    } catch (error) {
      console.log(error);
      this.setState("networkError");
    }
  }

  async syncDocumentMeta() {
    // Make an effort to ensure that data is persisted - It is up to the browser to decide whether this is granted.
    await navigator.storage.persist();

    let done: boolean = false;
    do {
      // Work out our last change and get any changes that have happened after it.
      const since = (await db.syncmeta.where("key").equals("syncUpTo").first())?.val;
      const changes = await this.getChanges(since);
      if (changes.length > 0) {
        await db.transaction("rw", ["documents", "files", "syncmeta"], async (tx) => {
          const nonDeletes = changes.filter((ch) => !ch.deleted);
          // Non-deletions get translated into puts
          const existingDocs = await tx.documents.bulkGet(nonDeletes.map((d) => d.id));
          const puts: Document[] = existingDocs.map((existing, idx) => {
            const merged = existing
              ? {
                  ...existing,
                  ...nonDeletes[idx],
                }
              : nonDeletes[idx];
            return merged;
          });
          await tx.documents.bulkPut(puts);

          const deletedDocs = changes.filter((ch) => ch.deleted);
          if (deletedDocs.length > 0) {
            const keys = deletedDocs.map((d) => d.key);
            const ids = deletedDocs.map((d) => d.id);
            console.log("deleting", keys);
            await tx.documents.bulkDelete(ids);
            await tx.files.bulkDelete(keys);
          }
          tx.syncmeta.put({ key: "syncUpTo", val: changes[changes.length - 1].seq });
        });
      }

      done = changes.length === 0;
    } while (!done);

    const appConfig = await readConfiguration("meta/config.json", defaultAppConfiguration);

    // We auto download any changed file, or any special (meta/) file that hasn't already been downloaded.
    await db.transaction("rw", db.documents, async () => {
      const toDownload: Document[] = [];
      for (const doc of await db.documents.toArray()) {
        if (shouldDocDownload(doc, appConfig)) {
          console.log("Adding", doc.key, "to download queue", doc.stored_etag, doc.etag, doc.download_order);
          toDownload.push(doc);
        }
      }
      if (toDownload.length > 0) {
        await addToDownloadQueue(toDownload);
      }
    });
  }
}

function shouldDocDownload(doc: Document, appConfig: AppConfiguration) {
  if (doc.deleted) {
    return false;
  }
  if (doc.download_order) {
    // It is already queued for download, no-need to re-queue
    return false;
  }

  const downloadedAndChanged = doc.stored_etag && doc.stored_etag !== doc.etag;
  // console.log("Checking", doc.key, doc.stored_etag, doc.etag, downloadedAndChanged);

  // All metadata, and the help document auto-download, if it isn't present, or if it has changed.
  if ((!doc.stored_etag || downloadedAndChanged) && (doc.key.startsWith("meta/") || doc.key === appConfig.helpDoc)) {
    return true;
  }

  // Files that were previously downloaded but have now changed auto-download
  return downloadedAndChanged;
}

export function setDiskSpaceLimited(val: boolean) {
  DocListSynchroniser.instance.setDiskSpaceLimited(val);
}

export function requestDocListSync() {
  // TODO put a mutex or promise around this.
  console.log("Requesting sync");
  DocListSynchroniser.instance.doSync();
}

export const DocumentsSynchroniserContext = createContext<DocumentSyncStatus>({ status: "unauthorized", diskSpaceLimited: false });

export function useDocumentSyncStatus() {
  return useContext(DocumentsSynchroniserContext);
}

export default function DocumentSynchroniserProvider(props: { children: ReactNode }) {
  const [status, setStatus] = useState<DocumentSyncStatus>({
    status: DocListSynchroniser.instance.status,
    diskSpaceLimited: DocListSynchroniser.instance.diskSpaceLimited,
  });
  const config = useAppConfiguration();

  useEffect(() => {
    let syncTimer: any = undefined;

    if (config.syncPeriod) {
      syncTimer = setInterval(() => {
        requestDocListSync();
      }, config.syncPeriod * 1000);
    }
    DocListSynchroniser.instance.setStateListener(setStatus);

    return () => {
      if (syncTimer) {
        clearInterval(syncTimer);
      }
      DocListSynchroniser.instance.setStateListener(undefined);
    };
  }, [config.syncPeriod]);

  return <DocumentsSynchroniserContext.Provider value={status}>{props.children}</DocumentsSynchroniserContext.Provider>;
}
